"use client";

import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import {
  type AiDebug,
  type AnswerAnalysis,
  type ArtistQuestion,
  type BossQuestionsResponse,
  type FanChatRequest,
  type FanChatResponse,
  type Member,
  parseArtistQuestionsResponse,
  parseBossQuestionsResponse,
  parseFanChatResponse,
} from "@/lib/contracts";
import {
  buildFallbackBossQuestions,
  buildArtistQuestion,
  getArtistData,
  members,
} from "@/lib/game-data";

type Screen = "start" | "profile" | "forum" | "ending";
type QuestionType = "opinion" | "receipt";
type Message = {
  id: string;
  type: "fan" | "user" | "system";
  name?: string;
  text: string;
};

type GameState = {
  username: string;
  artist: string;
  photo: string;
  stage: number;
  bossQuestion: number;
  suspicion: number;
  credibility: number;
  rewards: string[];
  currentPrompt: string;
  currentQuestionType: QuestionType;
  currentAnswer: string;
  artistQuestions: ArtistQuestion[];
  bossPrompts: string[];
};

const initialState: GameState = {
  username: "",
  artist: "",
  photo: "",
  stage: 0,
  bossQuestion: 0,
  suspicion: 0,
  credibility: 50,
  rewards: [],
  currentPrompt: "",
  currentQuestionType: "opinion",
  currentAnswer: "",
  artistQuestions: [],
  bossPrompts: [],
};

const initialDebug: AiDebug = {
  mode: "ready",
  classification: "WAITING",
  searchStatus: "WAITING",
  confidence: 0,
  evidence: ["Waiting for your first DM."],
  sources: [],
};

const LOCAL_APPROVAL_TYPING_MS = 3200;

export function LarpSimulator() {
  const [screen, setScreen] = useState<Screen>("start");
  const [state, setState] = useState<GameState>(initialState);
  const [usernameInput, setUsernameInput] = useState("");
  const [artistInput, setArtistInput] = useState("");
  const [messageInput, setMessageInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [isBusy, setIsBusy] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [aiDebug, setAiDebug] = useState<AiDebug>(initialDebug);
  const [toast, setToast] = useState<string>("");
  const chatLogRef = useRef<HTMLDivElement | null>(null);
  const messageFormRef = useRef<HTMLFormElement | null>(null);
  const toastTimerRef = useRef<number | null>(null);

  function showToast(text: string) {
    setToast(text);
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(""), 3400);
  }

  function flavorClick(line: string) {
    return (event: { preventDefault: () => void }) => {
      event.preventDefault();
      showToast(line);
    };
  }

  function handleForumSearch(value: FormDataEntryValue | null) {
    const query = typeof value === "string" ? value.trim() : "";
    if (!query) {
      showToast("type something. the indexer hates empty queries.");
      return;
    }
    showToast(buildForumSearchReply(query, state.artist));
  }

  const hasSentDm = messages.some((message) => message.type === "user");
  const fanThreadReady =
    state.stage > 0 &&
    state.bossQuestion === 0 &&
    messages.some((message) => message.text.includes("probably real"));
  const activeBossPrompts = getBossPrompts(state);
  const bossThreadReady = state.stage >= members.length && state.bossQuestion >= activeBossPrompts.length;
  const canAdvance = !isBusy && screen === "forum" && (fanThreadReady || bossThreadReady);
  const displayStage = fanThreadReady ? state.stage - 1 : state.stage;
  const activeMember = displayStage < members.length ? members[displayStage] : null;
  const activeDisplay = activeMember
    ? {
        label: `DM ${displayStage + 1} of 5`,
        name: `${activeMember.name} (${activeMember.title})`,
        bio: activeMember.bio,
        avatar: String(displayStage + 1).padStart(2, "0"),
      }
    : {
        label: "Final DM",
        name: "site_owner (Fandom Owner)",
        bio: "Oldest account on the forum. The join date says 'before time'.",
        avatar: "!!",
      };

  useEffect(() => {
    if (!chatLogRef.current) return;
    chatLogRef.current.scrollTop = chatLogRef.current.scrollHeight;
  }, [messages]);

  function previewPhoto(file: File | undefined) {
    if (!file) return;
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      setState((current) => ({ ...current, photo: String(reader.result) }));
    });
    reader.readAsDataURL(file);
  }

  async function createProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isProfileLoading) return;
    setIsProfileLoading(true);
    const artist = cleanArtistName(artistInput);
    const researched = await tryArtistQuestions(artist);
    const nextState: GameState = {
      ...initialState,
      username: usernameInput.trim() || "lurker",
      artist,
      photo: state.photo,
      artistQuestions: researched?.questions ?? [],
    };

    setState(nextState);
    setAiDebug(
      researched
        ? {
            mode: "artist_research",
            classification: "UNKNOWN",
            searchStatus: researched.searched ? "SUPPORTED" : "UNKNOWN",
            confidence: 100,
            evidence: researched.evidence.length
              ? researched.evidence
              : [`Pulled up the ${artist} board before opening DMs.`],
            sources: researched.sources,
          }
        : initialDebug,
    );
    setScreen("forum");
    setIsProfileLoading(false);
    void openCurrentThread(nextState);
  }

  async function openCurrentThread(baseState = state) {
    setMessageInput("");

    if (baseState.stage < members.length) {
      const member = members[baseState.stage];
      const question =
        baseState.artistQuestions[baseState.stage] ??
        buildArtistQuestion(baseState.artist, baseState.stage);
      const nextState = {
        ...baseState,
        currentPrompt: question.text,
        currentQuestionType: question.type,
        currentAnswer: "",
        suspicion: 0,
      };
      setState(nextState);
      setMessages([
        systemMessage(`${member.name} started a private thread with you.`),
        fanMessage(member, `${member.style}: ${question.text}`),
      ]);
      return;
    }

    await openBossThread(baseState);
  }

  async function openBossThread(baseState = state) {
    setMessages([
      systemMessage("site_owner has invited you to the admin chat."),
      fanMessage({ name: "site_owner" }, "give me a second. checking the old indexes before i test you."),
    ]);

    const generated = await tryBossQuestions(baseState.artist);
    const prompts = generated?.questions.length
      ? generated.questions
      : buildFallbackBossQuestions(baseState.artist);
    const prompt = prompts[baseState.bossQuestion] ?? prompts[0] ?? "";
    setState({
      ...baseState,
      bossPrompts: prompts,
      currentPrompt: prompt,
      currentQuestionType: "receipt",
      currentAnswer: "",
    });
    setMessages([
      systemMessage("site_owner has invited you to the admin chat."),
      fanMessage({ name: "site_owner" }, prompt),
    ]);
  }

  async function sendMessage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const answer = messageInput.trim();
    if (!answer || isBusy) return;

    appendMessage(userMessage(state.username, answer));
    setMessageInput("");

    if (state.stage >= members.length) {
      resolveBossAnswer(answer);
      return;
    }

    setIsBusy(true);
    const typingId = crypto.randomUUID();
    appendMessage({
      id: typingId,
      type: "system",
      text: `${members[state.stage]?.name ?? "fan"} is typing...`,
    });

    try {
      await resolveFanAnswer(answer, typingId);
    } finally {
      setIsBusy(false);
    }
  }

  function handleMessageKeydown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (isBusy) return;
    messageFormRef.current?.requestSubmit();
  }

  async function resolveFanAnswer(answer: string, typingId: string) {
    const member = members[state.stage];
    if (!member) return;

    const typingStartedAt = Date.now();
    const previousAnswer = state.currentAnswer;
    const analysis = analyzeAnswer(answer, state.artist);
    const actualFact = findActualFact(answer, state.artist);
    const nextCurrentAnswer = previousAnswer ? `${previousAnswer} ${answer}` : answer;
    const likelyLarp = isLikelyLarpAnswer(answer, analysis, actualFact);
    const score =
      scoreBelievability(answer, analysis, state.currentQuestionType) +
      (actualFact ? 18 : 0) +
      consistencyBonus(previousAnswer || state.currentPrompt, answer) -
      state.suspicion * 5;
    const requiredScore = getRequiredScore(state);
    const realFactNeedsFollowUp =
      Boolean(actualFact) && state.suspicion < getRequiredFactFollowUps(state);
    const larpNeedsFollowUp =
      likelyLarp && state.suspicion < getRequiredLarpFollowUps(state, analysis);
    const confidentLarp =
      likelyLarp &&
      hasConvincingUnverifiedDetail(answer, score, requiredScore, state);
    const opinionCandidate = isOpinionAnswer(
      answer,
      analysis,
      actualFact,
      larpNeedsFollowUp,
      confidentLarp,
      state,
    );
    const opinionNeedsFollowUp =
      opinionCandidate &&
      (state.suspicion < getRequiredOpinionFollowUps(state) ||
        !hasSufficientOpinionReason(answer, analysis, state));
    const knownFactPass =
      Boolean(actualFact) &&
      !realFactNeedsFollowUp &&
      (score >= requiredScore - 18 || state.stage < 2);

    const aiDecision =
      buildInstantOpinionDecision({
        answer,
        member,
        analysis,
        score,
        requiredScore,
        actualFact,
        state,
        realFactNeedsFollowUp,
        larpNeedsFollowUp,
      }) ??
      (await tryAiFanReply({
        answer,
        previousAnswer,
        member,
        analysis,
        score,
        requiredScore,
        confidentLarp,
        actualFact,
        larpNeedsFollowUp,
        nextCurrentAnswer,
      }));

    if (aiDecision) {
      await holdTypingFor(typingStartedAt, getDecisionTypingMs(aiDecision, state));
      removeMessage(typingId);
      setAiDebug({
        mode:
          aiDecision.provider === "local_fallback"
            ? "instant_opinion"
            : aiDecision.searched
              ? "web_search"
              : "ai_fast_path",
        classification: aiDecision.classification,
        searchStatus: aiDecision.searchStatus,
        confidence: aiDecision.confidence,
        evidence: aiDecision.evidence,
        sources: aiDecision.sources,
      });

      if (
        aiDecision.passed &&
        aiDecision.classification === "REAL_FACT" &&
        state.suspicion < getRequiredFactFollowUps(state)
      ) {
        setState((current) => ({
          ...current,
          currentAnswer: nextCurrentAnswer,
          suspicion: current.suspicion + 1,
          credibility: Math.max(0, current.credibility - 2),
        }));
        appendMessage(fanMessage(member, buildRealFactFollowUp(member, actualFact, analysis, state)));
        return;
      }

      if (
        aiDecision.passed &&
        aiDecision.classification === "PLAUSIBLE_LARP" &&
        (state.suspicion < getRequiredLarpFollowUps(state, analysis) ||
          (likelyLarp && !confidentLarp))
      ) {
        setState((current) => ({
          ...current,
          currentAnswer: nextCurrentAnswer,
          suspicion: current.suspicion + 1,
          credibility: Math.max(0, current.credibility - 4),
        }));
        appendMessage(fanMessage(member, buildLarpFollowUp(member, analysis, state)));
        return;
      }

      if (
        aiDecision.passed &&
        aiDecision.classification === "OPINION" &&
        (state.suspicion < getRequiredOpinionFollowUps(state) ||
          !hasSufficientOpinionReason(answer, analysis, state))
      ) {
        setState((current) => ({
          ...current,
          currentAnswer: nextCurrentAnswer,
          suspicion: current.suspicion + 1,
          credibility: Math.max(0, current.credibility - 3),
        }));
        appendMessage(fanMessage(member, buildOpinionFollowUp(analysis, state, answer)));
        return;
      }

      if (aiDecision.passed) {
        passCurrentStage(member, aiDecision.reply || "ok, you convinced me.");
        return;
      }

      setState((current) => ({
        ...current,
        currentAnswer: nextCurrentAnswer,
        suspicion: current.suspicion + 1,
        credibility: Math.max(0, current.credibility - 5),
      }));
      appendMessage(fanMessage(member, aiDecision.reply || buildFallbackFollowUp(member, analysis, state)));
      return;
    }

    if (larpNeedsFollowUp) {
      await holdTypingFor(typingStartedAt, 1700);
      removeMessage(typingId);
      setAiDebug({
        mode: "local_fallback",
        classification: "PLAUSIBLE_LARP",
        searchStatus: "NO_WEB_SEARCH",
        confidence: Math.min(100, Math.round((score / Math.max(requiredScore, 1)) * 100)),
        evidence: ["Unverified fandom detail; asked for more context before approving."],
        sources: [],
      });
      setState((current) => ({
        ...current,
        currentAnswer: nextCurrentAnswer,
        suspicion: current.suspicion + 1,
        credibility: Math.max(0, current.credibility - 4),
      }));
      appendMessage(fanMessage(member, buildLarpFollowUp(member, analysis, state)));
      return;
    }

    if (realFactNeedsFollowUp) {
      await holdTypingFor(typingStartedAt, 1400);
      removeMessage(typingId);
      setAiDebug({
        mode: "local_fallback",
        classification: "REAL_FACT",
        searchStatus: "LOCAL_KNOWN_FACT",
        confidence: Math.min(100, Math.round((score / Math.max(requiredScore, 1)) * 100)),
        evidence: [`Recognized "${actualFact}", but asked a deeper fandom follow-up.`],
        sources: [],
      });
      setState((current) => ({
        ...current,
        currentAnswer: nextCurrentAnswer,
        suspicion: current.suspicion + 1,
        credibility: Math.max(0, current.credibility - 2),
      }));
      appendMessage(fanMessage(member, buildRealFactFollowUp(member, actualFact, analysis, state)));
      return;
    }

    if (opinionNeedsFollowUp) {
      await holdTypingFor(typingStartedAt, 1400);
      removeMessage(typingId);
      setAiDebug({
        mode: "local_fallback",
        classification: "OPINION",
        searchStatus: "OPINION",
        confidence: Math.min(100, Math.round((score / Math.max(requiredScore, 1)) * 100)),
        evidence: ["Personal taste answer recognized; asked a deeper follow-up before approval."],
        sources: [],
      });
      setState((current) => ({
        ...current,
        currentAnswer: nextCurrentAnswer,
        suspicion: current.suspicion + 1,
        credibility: Math.max(0, current.credibility - 3),
      }));
      appendMessage(fanMessage(member, buildOpinionFollowUp(analysis, state, answer)));
      return;
    }

    if (
      (!opinionCandidate && !likelyLarp && score >= requiredScore) ||
      (opinionCandidate && !opinionNeedsFollowUp && score >= requiredScore - 8) ||
      confidentLarp ||
      knownFactPass
    ) {
      await holdTypingFor(typingStartedAt, LOCAL_APPROVAL_TYPING_MS);
      removeMessage(typingId);
      setAiDebug({
        mode: "local_fallback",
        classification: actualFact
          ? "REAL_FACT"
          : opinionCandidate
            ? "OPINION"
            : confidentLarp
            ? "PLAUSIBLE_LARP"
            : "LOCAL_PASS",
        searchStatus: actualFact
          ? "LOCAL_KNOWN_FACT"
          : opinionCandidate
            ? "OPINION"
            : "NO_WEB_SEARCH",
        confidence: Math.min(100, Math.round((score / Math.max(requiredScore, 1)) * 100)),
        evidence: [
          actualFact
            ? `Matched built-in known fact: ${actualFact}`
            : opinionCandidate
              ? "Personal taste answer survived the required follow-up check."
              : "Unverified fandom detail had enough specificity and conviction.",
        ],
        sources: [],
      });
      passCurrentStage(
        member,
        buildAcceptanceReply(member, analysis, {
          confidentLarp,
          actualFact,
          questionType: state.currentQuestionType,
        }),
      );
      return;
    }

    await holdTypingFor(typingStartedAt, state.currentQuestionType === "receipt" ? 1500 : 1000);
    removeMessage(typingId);
    setAiDebug({
      mode: "local_fallback",
      classification: "WEAK_OR_NEEDS_MORE",
      searchStatus: "NO_WEB_SEARCH",
      confidence: Math.min(100, Math.round((score / Math.max(requiredScore, 1)) * 100)),
      evidence: ["No web-search response available; local logic asked a follow-up."],
      sources: [],
    });
    setState((current) => ({
      ...current,
      currentAnswer: nextCurrentAnswer,
      suspicion: current.suspicion + 1,
      credibility: Math.max(0, current.credibility - 8),
    }));
    appendMessage(fanMessage(member, buildFallbackFollowUp(member, analysis, state)));
  }

  function passCurrentStage(member: Member, reply: string) {
    const nextStage = state.stage + 1;
    const rewards = state.rewards.includes(member.reward)
      ? state.rewards
      : [...state.rewards, member.reward];

    setState((current) => ({
      ...current,
      stage: nextStage,
      rewards,
      credibility: Math.min(100, current.credibility + 9 + current.suspicion),
    }));
    appendMessage(fanMessage(member, reply));
    appendMessage(systemMessage(`${member.name} marked you as "probably real".`));
  }

  async function tryAiFanReply(context: {
    answer: string;
    previousAnswer: string;
    member: Member;
    analysis: AnswerAnalysis;
    score: number;
    requiredScore: number;
    confidentLarp: boolean;
    actualFact: string | null;
    larpNeedsFollowUp: boolean;
    nextCurrentAnswer: string;
  }): Promise<FanChatResponse | null> {
    const payload: FanChatRequest = {
      artist: state.artist,
      username: state.username,
      stage: state.stage,
      suspicion: state.suspicion,
      currentPrompt: state.currentPrompt,
      currentQuestionType: state.currentQuestionType,
      currentAnswer: context.nextCurrentAnswer,
      rewards: state.rewards,
      member: context.member,
      playerAnswer: context.answer,
      analysis: context.analysis,
      actualFact: context.actualFact,
      larpNeedsFollowUp: context.larpNeedsFollowUp,
      localScore: Math.round(context.score),
      requiredScore: context.requiredScore,
      confidentLarp: context.confidentLarp,
    };

    try {
      const response = await fetch(apiEndpoint("/api/fan-chat"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) return null;
      return parseFanChatResponse(await response.json());
    } catch {
      return null;
    }
  }

  async function tryBossQuestions(artist: string): Promise<BossQuestionsResponse | null> {
    try {
      const response = await fetch(apiEndpoint("/api/boss-questions"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ artist }),
      });
      if (!response.ok) return null;
      return parseBossQuestionsResponse(await response.json());
    } catch {
      return null;
    }
  }

  async function tryArtistQuestions(artist: string): Promise<{
    questions: ArtistQuestion[];
    evidence: string[];
    sources: AiDebug["sources"];
    searched: boolean;
  } | null> {
    try {
      const response = await fetch(apiEndpoint("/api/artist-questions"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ artist }),
      });
      if (!response.ok) return null;
      return parseArtistQuestionsResponse(await response.json());
    } catch {
      return null;
    }
  }

  function resolveBossAnswer(answer: string) {
    const analysis = analyzeAnswer(answer, state.artist);
    const bossPrompts = getBossPrompts(state);
    const reply = buildBossReply(answer, analysis, state.bossQuestion);
    const nextBossQuestion = state.bossQuestion + 1;

    setState((current) => ({
      ...current,
      bossQuestion: nextBossQuestion,
      credibility: Math.min(100, current.credibility + 5),
    }));
    appendMessage(fanMessage({ name: "site_owner" }, reply));

    if (nextBossQuestion < bossPrompts.length) {
      const nextPrompt = bossPrompts[nextBossQuestion] ?? "";
      setState((current) => ({ ...current, currentPrompt: nextPrompt }));
      appendMessage(fanMessage({ name: "site_owner" }, nextPrompt));
    } else {
      appendMessage(
        fanMessage(
          { name: "site_owner" },
          "alright. cards on the table: every final check was bullshit. i built gates out of scraps because i was terrified someone would love this harder than me.",
        ),
      );
      appendMessage(
        fanMessage(
          { name: "site_owner" },
          `i am the ultimate larper. sorry, ${state.username}. admin status is yours now. do not make the next poor bastard earn it too easily.`,
        ),
      );
      appendMessage(systemMessage(`${state.username} received site_owner status.`));
    }
  }

  function showEnding() {
    setScreen("ending");
  }

  function resetGame() {
    setState(initialState);
    setMessages([]);
    setAiDebug(initialDebug);
    setMessageInput("");
    setIsProfileLoading(false);
    setScreen("start");
  }

  function appendMessage(message: Message) {
    setMessages((current) => [...current, message]);
  }

  function removeMessage(id: string) {
    setMessages((current) => current.filter((message) => message.id !== id));
  }

  const rewards = state.rewards.length ? state.rewards : ["empty inventory"];

  return (
    <>
      {screen !== "start" && (
        <header className="site-chrome">
          <div className="topbar">
            <b>LarpSpace.com</b>
            <a href="#" onClick={flavorClick("Home page is just this. the rest is a CSS lie.")}>Home</a>
            <a href="#" onClick={flavorClick("the wider web cannot help you here. stay in the thread.")}>The Web</a>
            <a href="#" onClick={flavorClick("your LarpSpace profile is the DM you are typing rn. update it later.")}>My LarpSpace</a>
            <form
              className="search-box"
              aria-label="Forum search"
              onSubmit={(event) => {
                event.preventDefault();
                handleForumSearch(new FormData(event.currentTarget).get("q"));
              }}
            >
              <input type="search" name="q" aria-label="Search" />
              <button type="submit">Search</button>
            </form>
            <span className="topbar-right">Help | SignOut</span>
          </div>
          <nav className="navstrip" aria-label="Forum navigation">
            {["Home", "Browse", "Search", "Invite", "Mail", "Blog", "Favorites", "Forum", "Groups", "Music"].map((item) => (
              <a href="#" key={item} onClick={flavorClick(navstripFlavor(item))}>{item}</a>
            ))}
          </nav>
        </header>
      )}

      {toast && (
        <div className="larp-toast" role="status" aria-live="polite">{toast}</div>
      )}

      <main className={`shell ${screen === "start" ? "shell-start" : ""}`}>
        {screen === "start" && (
          <section className="start-screen" id="startScreen">
            <div className="start-marquee">
              <span>READ THIS BEFORE REGISTERING</span>
              <span>NO MOVIE / TV / GAME FANDOMS</span>
              <span>MUSIC ARTISTS ONLY</span>
            </div>

            <div className="start-window">
              <div className="window-title">
                <span className="mini-folder" />
                <b>START HERE - LarpSpace.com Setup Wizard</b>
                <span className="window-buttons" aria-hidden="true"><i /><i /><i /></span>
              </div>

              <div className="start-hero">
                <p className="start-badge">fandom forum simulation</p>
                <h1>Larp Simulator</h1>
                <p className="dek">
                  You are about to infiltrate a fake old-web music forum. The people
                  DMing you are simulated fans. They want to know if you actually belong.
                </p>
              </div>

              <div className="start-layout">
                <div className="start-icon-stack" aria-hidden="true">
                  <span className="pixel-icon user-icon" />
                  <span className="pixel-icon music-icon" />
                  <span className="pixel-icon modem-icon" />
                  <span className="pixel-icon question-icon" />
                </div>

                <div className="start-rules" aria-label="Game rules">
                  <section className="start-rule">
                    <h2>1. Create a forum profile.</h2>
                    <p>Choose a username, profile photo, and the fandom you want to sneak into.</p>
                  </section>
                  <section className="start-rule important">
                    <h2>2. Pick a musical artist only.</h2>
                    <p>The fandom must be a real singer, rapper, band, producer, composer, DJ, or music group.</p>
                  </section>
                  <section className="start-rule">
                    <h2>3. Survive five private DMs.</h2>
                    <p>Each fan is more dedicated than the last. They ask artist-specific questions and follow up if something feels thin.</p>
                  </section>
                  <section className="start-rule">
                    <h2>4. Larping is part of the game.</h2>
                    <p>You can tell the truth or invent secret lore. Fake answers need conviction, details, and a story that sounds like old forum drama.</p>
                  </section>
                </div>
              </div>

              <div className="start-warning">
                <b>Win condition:</b> earn trust through real knowledge, strong taste, or convincing fake lore.
                After five fans, the forum owner tests you with their own fake history. Then the cycle continues.
              </div>

              <div className="start-actions">
                <button type="button" onClick={() => setScreen("profile")}>Start Game</button>
              </div>
            </div>
          </section>
        )}

        {screen === "profile" && (
          <section className="profile-screen" id="profileScreen">
            <header className="masthead">
              <div>
                <p className="eyebrow">new member signup</p>
                <h1>Larp Simulator</h1>
                <p className="dek">
                  Make a profile, join any music fandom, then survive the DMs.
                  Real answers work. Fake lore works too, if you can make it
                  sound like everyone else somehow missed the thread.
                </p>
              </div>
            </header>

            <DesktopWindow />

            <form className="profile-form" onSubmit={createProfile}>
              <section className="profile-card">
                <h2>Profile Photo</h2>
                <div className="photo-preview" id="photoPreview">
                  <img src={state.photo || "/assets/larp-sigil.svg"} alt="" id="previewImage" />
                </div>
                <label htmlFor="photoInput">Profile photo</label>
                <input
                  id="photoInput"
                  name="photo"
                  type="file"
                  accept="image/*"
                  onChange={(event) => previewPhoto(event.currentTarget.files?.[0])}
                />
                <ContactBox
                  title="Contacting New User"
                  onItemClick={(item) => showToast(contactBoxFlavor(item, "new_user"))}
                />
              </section>

              <section className="signup-card">
                <h2>Create Your Forum Profile</h2>
                <div className="field">
                  <label htmlFor="usernameInput">Forum username</label>
                  <input
                    id="usernameInput"
                    name="username"
                    autoComplete="off"
                    placeholder="xX_lorekeeper_2007_Xx"
                    required
                    value={usernameInput}
                    onChange={(event) => setUsernameInput(event.currentTarget.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="artistInput">Fandom</label>
                  <input
                    id="artistInput"
                    name="artist"
                    autoComplete="off"
                    placeholder="Radiohead, Beyonce, Mitski, BTS..."
                    required
                    value={artistInput}
                    onChange={(event) => setArtistInput(event.currentTarget.value)}
                  />
                </div>
                <div className="rules">
                  <p className="box-title">LarpSpace Bulletin</p>
                  <p><b>Forum rule:</b> nobody can prove you are lying if your lore has enough dates, places, names, and weirdly specific emotional stakes.</p>
                  <p><b>Win condition:</b> convince five bigger fans, collect rewards, then face the owner.</p>
                </div>
                <button type="submit" disabled={isProfileLoading}>
                  {isProfileLoading ? "Entering Forum..." : "Create Profile"}
                </button>
              </section>
            </form>
          </section>
        )}

        {screen === "forum" && (
          <section className="forum" id="forum" aria-live="polite">
            <aside className="sidebar">
              <section className="user-panel">
                <div className="photo-preview small" id="sidebarPhoto">
                  {state.photo ? <img src={state.photo} alt="" /> : <span>{initials(state.username)}</span>}
                </div>
                <div>
                  <p className="eyebrow">logged in as</p>
                  <h2 id="profileName">{state.username}</h2>
                  <p id="profileFandom">{state.artist} fandom</p>
                </div>
              </section>

              <ContactBox
                title="Contacting You"
                live
                onItemClick={(item) => showToast(contactBoxFlavor(item, activeMember?.name ?? "site_owner"))}
              />

              <section className="board-panel">
                <p className="panel-title">DM queue</p>
                <div className="stage-list" id="stageList">
                  {[...members, { name: "site_owner", title: "Fandom Owner" }].map((member, index) => (
                    <div
                      className={`stage-item ${index === state.stage ? "active" : ""} ${index < state.stage ? "done" : ""}`}
                      key={member.name}
                    >
                      <span>{member.name}</span>
                      <b>{index < state.stage ? "trusted" : index === state.stage ? "typing" : "offline"}</b>
                    </div>
                  ))}
                </div>
              </section>

              <section className="board-panel">
                <p className="panel-title">Credibility</p>
                <div className="meter">
                  <span style={{ width: `${Math.max(0, Math.min(100, state.credibility))}%` }} />
                </div>
                <b id="credibilityText">{Math.max(0, Math.min(100, state.credibility))}%</b>
              </section>

              <section className="board-panel">
                <p className="panel-title">Rewards</p>
                <ul id="rewardsList">
                  {rewards.map((reward) => <li key={reward}>{reward}</li>)}
                </ul>
              </section>

              {hasSentDm && (
                <section className="board-panel ai-panel">
                  <p className="panel-title">Forum readout</p>
                  <AiDebugPanel debug={aiDebug} />
                </section>
              )}
            </aside>

            <section className="chat-window">
              <header className="chat-topbar">
                <div className="fan-avatar" id="fanAvatar">{activeDisplay.avatar}</div>
                <div>
                  <p className="eyebrow" id="stageLabel">{activeDisplay.label}</p>
                  <h2 id="fanName">{activeDisplay.name}</h2>
                  <p id="fanBio">{activeDisplay.bio}</p>
                </div>
              </header>

              <div className="network-banner">This user is in your extended fandom network</div>

              <div className="chat-log" id="chatLog" ref={chatLogRef}>
                {messages.map((message) => (
                  message.type === "system" ? (
                    <p className="system-message" key={message.id}>{message.text}</p>
                  ) : (
                    <article className={`message ${message.type}`} key={message.id}>
                      <p className="message-meta">
                        {message.type === "user" && state.photo && (
                          <img className="message-photo" src={state.photo} alt="" />
                        )}
                        {message.name}
                      </p>
                      <p>{message.text}</p>
                    </article>
                  )
                ))}
                {canAdvance && (
                  <button
                    type="button"
                    className="thread-button"
                    onClick={bossThreadReady ? showEnding : () => void openCurrentThread(state)}
                  >
                    {bossThreadReady ? "Next" : state.stage >= members.length ? "Face the owner" : "Open next DM"}
                  </button>
                )}
              </div>

              {!canAdvance && (
                <form className="message-form" onSubmit={sendMessage} ref={messageFormRef}>
                  <label htmlFor="messageInput" id="messageLabel">Reply</label>
                  <textarea
                    id="messageInput"
                    rows={4}
                    placeholder="Answer honestly or larp. Specifics are your armor."
                    required
                    disabled={isBusy}
                    value={messageInput}
                    onChange={(event) => setMessageInput(event.currentTarget.value)}
                    onKeyDown={handleMessageKeydown}
                  />
                  <div className="action-row">
                    <button type="button" className="secondary" onClick={resetGame}>Log Out</button>
                    <button type="submit" disabled={isBusy}>{isBusy ? "Sending..." : "Send DM"}</button>
                  </div>
                </form>
              )}
            </section>
          </section>
        )}

        {screen === "ending" && (
          <section className="ending" id="ending">
            <div className="cutscene">
              <img src="/assets/larp-sigil.svg" alt="" className="ending-sigil" />
              <p className="eyebrow">admin broadcast</p>
              <h2>The cycle continues.</h2>
              <div id="endingText">
                <p>The old owner disappears from the member list. Their profile still says online.</p>
                <p>A new user joins the forum. Their first post says: "hi, is this the real {state.artist} fandom?"</p>
                <p><b>You are the owner now.</b></p>
              </div>
              <button type="button" id="playAgainButton" onClick={resetGame}>Make New Profile</button>
            </div>
          </section>
        )}
      </main>
    </>
  );
}

function DesktopWindow() {
  const icons = [
    ["user-icon", "For New Users Only"],
    ["modem-icon", "Internet Fandom Connectivity"],
    ["music-icon", "32-bit Fan Lore Drivers"],
    ["folder-icon", "Profile Shareware"],
    ["question-icon", "Larp Hyper-Glossary"],
    ["cd-icon", "Bootleg CD-ROM"],
    ["search-icon", "Search the Site"],
    ["trophy-icon", "About the Owner"],
  ];

  return (
    <section className="desktop-window" aria-label="LarpSpace welcome launcher">
      <div className="window-title">
        <span className="mini-folder" />
        <b>Welcome to LarpSpace.com</b>
        <span className="window-buttons"><i /><i /><i /></span>
      </div>
      <div className="icon-grid" aria-hidden="true">
        {icons.map(([icon, label]) => (
          <div className="desktop-icon" key={label}>
            <span className={`pixel-icon ${icon}`} />
            <b>{label}</b>
          </div>
        ))}
      </div>
      <div className="window-status">
        <span>(c)1998 LarpSpace.com</span>
        <span>Webmaster Services by ultimate_larper</span>
      </div>
    </section>
  );
}

function ContactBox({
  title,
  live = false,
  onItemClick,
}: {
  title: string;
  live?: boolean;
  onItemClick?: (item: string) => void;
}) {
  const items = ["Send Message", "Add to Friends", "Instant Message", "Add to Group", "Forward Profile", "Block User"];
  return (
    <section className={`contact-box ${live ? "live" : ""}`}>
      <p className="box-title">{title}</p>
      <div className="contact-grid">
        {items.map((item) => (
          <button
            type="button"
            className="contact-action"
            key={item}
            onClick={() => onItemClick?.(item)}
          >
            {item}
          </button>
        ))}
      </div>
    </section>
  );
}

function AiDebugPanel({ debug }: { debug: AiDebug }) {
  const sources = debug.sources.filter((source) => source.url).slice(0, 3);
  const readout = getForumReadout(debug);

  return (
    <div id="aiDebug" className="ai-debug">
      <dl className="debug-list">
        <dt>thread</dt><dd>{readout.thread}</dd>
        <dt>reply type</dt><dd>{readout.replyType}</dd>
        <dt>receipts</dt><dd>{readout.receipts}</dd>
        <dt>trust</dt><dd>{Math.round(debug.confidence)}%</dd>
      </dl>
      <b>mod notes</b>
      <ul>
        {(debug.evidence.length ? debug.evidence : ["No notes yet."]).map((item) => (
          <li key={item}>{formatForumNote(item).slice(0, 160)}</li>
        ))}
      </ul>
      {sources.length > 0 && (
        <>
          <b>linked posts</b>
          <ul>
            {sources.map((source) => (
              <li key={source.url}>
                <a href={source.url} target="_blank" rel="noreferrer">
                  {source.title || source.url}
                </a>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function formatForumNote(note: string): string {
  const supported = note.match(/^Search supported (.+?) as real fandom knowledge\.$/i);
  if (supported) return `Old threads recognize ${supported[1]}.`;

  const verified = note.match(/^Verified (.+?) as real (.+?) fandom knowledge\.$/i);
  if (verified) return `${verified[1]} shows up in the old ${verified[2]} posts.`;

  const recognized = note.match(/^Recognized real fandom fact: (.+)\.$/i);
  if (recognized) return `${recognized[1]} is already part of the board lore.`;

  const couldNotVerify = note.match(/^Could not verify (.+?) as real (.+?) fandom knowledge\.$/i);
  if (couldNotVerify) return `Nobody has pinned down ${couldNotVerify[1]} in the ${couldNotVerify[2]} board yet.`;

  const matchedKnownFact = note.match(/^Matched built-in known fact: (.+)$/i);
  if (matchedKnownFact) return `${matchedKnownFact[1]} is familiar to longtime posters.`;

  if (/A real answer still needs a deeper follow-up/i.test(note)) {
    return "They want one more lived-in detail before trusting the profile.";
  }
  if (/Real fact survived the required follow-up check/i.test(note)) {
    return "The follow-up sounded lived-in enough for the thread.";
  }
  if (/Personal taste answer recognized; asked a deeper follow-up/i.test(note)) {
    return "The take landed, but they want one more layer.";
  }
  if (/Personal taste answer included enough reasoning/i.test(note)) {
    return "The reasoning has enough personal texture.";
  }
  if (/Personal taste answer needs a reason/i.test(note)) {
    return "A title alone is not enough for this crowd.";
  }
  if (/Unverified fandom detail; asked for more context/i.test(note)) {
    return "The detail sounds possible, but the thread wants another anchor.";
  }
  if (/Follow-up needs more concrete context/i.test(note)) {
    return "The thread still needs a cleaner receipt-shaped detail.";
  }
  if (/Unverified detail had enough specificity/i.test(note)) {
    return "The detail had enough texture to pass as buried board lore.";
  }
  if (/A named pick needs more than a vibe/i.test(note)) {
    return "A named pick needs more than a vibe here.";
  }
  if (/No web-search response available|local logic/i.test(note)) {
    return "The board is lagging, so the regulars asked for another detail.";
  }
  if (/Opinion answer; no receipt check needed/i.test(note)) {
    return "This one is about taste, not receipts.";
  }

  return note;
}

function getForumReadout(debug: AiDebug): {
  thread: string;
  replyType: string;
  receipts: string;
} {
  const threadLabels: Record<string, string> = {
    ready: "waiting",
    artist_research: "joining board",
    web_search: "checking old posts",
    ai_fast_path: "live DM",
    instant_opinion: "quick reply",
    local_fallback: "offline cache",
  };
  const replyLabels: Partial<Record<AiDebug["classification"], string>> = {
    WAITING: "none yet",
    UNKNOWN: "profile setup",
    OPINION: "personal take",
    REAL_FACT: "known lore",
    PLAUSIBLE_LARP: "unconfirmed lore",
    WEAK_OR_WRONG: "needs more detail",
    WEAK_OR_NEEDS_MORE: "needs more detail",
    LOCAL_PASS: "accepted",
  };
  const receiptLabels: Partial<Record<AiDebug["searchStatus"], string>> = {
    WAITING: "none yet",
    UNKNOWN: "board loading",
    SUPPORTED: "thread found",
    NOT_FOUND: "no thread found",
    CONTRADICTED: "thread disagrees",
    OPINION: "not needed",
    LOCAL_KNOWN_FACT: "known locally",
    NO_WEB_SEARCH: "not checked",
  };

  return {
    thread: threadLabels[debug.mode] ?? "live DM",
    replyType: replyLabels[debug.classification] ?? String(debug.classification).toLowerCase(),
    receipts: receiptLabels[debug.searchStatus] ?? String(debug.searchStatus).toLowerCase(),
  };
}

function fanMessage(member: Pick<Member, "name">, text: string): Message {
  return { id: crypto.randomUUID(), type: "fan", name: member.name, text: removeQuotationMarks(text) };
}

function userMessage(name: string, text: string): Message {
  return { id: crypto.randomUUID(), type: "user", name, text };
}

function systemMessage(text: string): Message {
  return { id: crypto.randomUUID(), type: "system", text };
}

function apiEndpoint(path: string): string {
  const apiWindow =
    typeof window !== "undefined"
      ? (window as Window & { LARP_API_BASE?: unknown })
      : null;
  const configured =
    apiWindow && typeof apiWindow.LARP_API_BASE === "string"
      ? apiWindow.LARP_API_BASE.trim()
      : "";

  if (!configured) return path;
  return `${configured.replace(/\/$/, "")}${path}`;
}

function getBossPrompts(state: GameState): string[] {
  return state.bossPrompts.length ? state.bossPrompts : buildFallbackBossQuestions(state.artist);
}

function getOpinionSubject(state: GameState, analysis: AnswerAnalysis): string {
  const anchor = describeReference(analysis.anchor);
  if (anchor && anchor !== "that detail") return anchor;
  const promptedSong = getPromptedSong(state);
  return promptedSong || "that song";
}

function getPromptedSong(state: GameState): string | null {
  const data = getArtistData(state.artist);
  const prompt = normalizeText(state.currentPrompt);
  return data.songs.find((song) => prompt.includes(normalizeText(song))) ?? null;
}

function buildFallbackFollowUp(
  member: Pick<Member, "name" | "title">,
  analysis: AnswerAnalysis,
  state: GameState,
): string {
  const anchor = describeReference(analysis.anchor || state.artist);
  if (state.currentQuestionType === "opinion") {
    return `${member.name === "newmodthrowaway" ? "hmm" : "wait"} i can see the ${anchor} angle, barely. give me one more sentence that does not smell like profile-fillout garbage.`;
  }

  if (analysis.actsLikeFact) {
    return `i am hovering over the reply box rn. ${anchor} sounds possible, but do not half-ass it: when, where, or what old upload/thread would make fans remember it?`;
  }

  return `not saying no, but ${anchor} is still blurry as hell for a ${member.title.toLowerCase()}. add one concrete detail and i will decide.`;
}

function buildLarpFollowUp(
  member: Pick<Member, "name" | "title">,
  analysis: AnswerAnalysis,
  state: GameState,
): string {
  const anchor = describeReference(analysis.anchor || "that secret detail");
  const prompts = [
    `${anchor} is new to me, wait. era, uploader, fallout. give me one or this is noise.`,
    `i have not run into ${anchor} before, but it has cursed old-thread energy. what upload, city, username, or argument points to it?`,
    `hold on, ${anchor} would explain a lot if it actually circulated. what context did people attach to it?`,
    `that is a wild detail. give me one more anchor around ${anchor}: era, source, fan argument, or why anyone gave a damn.`,
  ];
  return member.title.includes("Prosecutor") ? prompts[3] ?? prompts[0] : pick(prompts);
}

function buildOpinionFollowUp(analysis: AnswerAnalysis, state: GameState, answer?: string): string {
  if (answer && hasSufficientOpinionReason(answer, analysis, state)) {
    return pick([
      `that reaction tracks, fine. what exact part of the song is doing that to you?`,
      `ok, i get the feeling. what moment in the track hits hard enough to make you say it out loud?`,
      `yeah, that sounds attached. what sound, lyric, or shift is doing the damn work?`,
    ]);
  }

  if (/\bera\b/i.test(state.currentPrompt)) {
    return pick([
      `ok, but why that era specifically? sound, rollout, look, fan argument. pick your weapon.`,
      `i can work with that era pick, but i need the reason. what about it actually hits, or are we just naming eras now?`,
      `say more on the era. what song, performance, visual, or fandom moment makes you claim it?`,
    ]);
  }

  const subject = getOpinionSubject(state, analysis);
  return pick([
    `ok, but why ${subject}? give me the part that made you care, not the bumper sticker version.`,
    `i need one more sentence. what about ${subject} actually hits you?`,
    `that is a pick, not a reason. what sound, lyric, memory, or fan argument makes it matter?`,
  ]);
}

function buildRealFactFollowUp(
  member: Pick<Member, "name" | "title">,
  actualFact: string | null,
  analysis: AnswerAnalysis,
  state: GameState,
): string {
  const anchor = describeReference(actualFact || analysis.anchor || state.artist);
  const remaining = getRequiredFactFollowUps(state) - state.suspicion;
  if (state.currentQuestionType === "opinion") {
    return pick([
      `yeah, ${anchor} is real, congrats on knowing the door exists. what part makes it your pick?`,
      `ok, ${anchor} checks out. one layer deeper: what detail would make another fan argue with you about it?`,
      `sure, ${anchor} is real. ${remaining > 1 ? "give me two beats of reasoning, starting with" : "now tell me"} the part you actually replay.`,
    ]);
  }

  return pick([
    `${anchor} checks out, but i need the messy version. what era, performance, upload, or fan argument proves you are not just name-dropping?`,
    `ok, real detail. now connect ${anchor} to one specific fandom memory or controversy before i let you through.`,
    `i know ${anchor}, anon. that is why i am asking the second question: what tiny detail do fans fight over?`,
  ]);
}

function getDecisionTypingMs(decision: FanChatResponse, state: GameState): number {
  if (decision.provider !== "local_fallback") return 0;
  if (decision.passed) return LOCAL_APPROVAL_TYPING_MS;
  return 1100;
}

async function holdTypingFor(startedAt: number, minimumMs: number): Promise<void> {
  const remaining = minimumMs - (Date.now() - startedAt);
  if (remaining <= 0) return;
  await new Promise((resolve) => window.setTimeout(resolve, remaining));
}

function findActualFact(answer: string, artist: string): string | null {
  const data = getArtistData(artist);
  const haystack = normalizeText(answer);
  const facts = [...data.songs, ...data.albums, ...data.lore];
  return facts.find((fact) => {
    const normalized = normalizeText(fact);
    if (normalized.length < 4) return false;
    return haystack.includes(normalized);
  }) ?? null;
}

function getRequiredScore(state: GameState): number {
  const base = state.currentQuestionType === "opinion" ? 22 : 30;
  return base + state.stage * 9 + state.suspicion * 3;
}

function getLarpScoreRequirement(state: GameState): number {
  return 28 + state.stage * 8 + state.suspicion * 2;
}

function getRequiredFactFollowUps(state: GameState): number {
  return state.stage >= 3 ? 2 : 1;
}

function getRequiredOpinionFollowUps(state: GameState): number {
  return state.stage >= 3 ? 2 : 1;
}

function getRequiredLarpFollowUps(state: GameState, analysis?: AnswerAnalysis): number {
  return (state.stage >= 3 ? 2 : 1) +
    (state.currentQuestionType === "opinion" && analysis?.actsLikeFact ? 1 : 0);
}

function hasSufficientOpinionReason(
  answer: string,
  analysis: AnswerAnalysis,
  state: GameState,
): boolean {
  const words = answer.split(/\s+/).filter(Boolean);
  const hasReason =
    analysis.hasFeeling ||
    /\b(because|since|when|while|the way|it feels|it sounds|reminds me|makes me|gets me|hits|why)\b/i.test(answer);
  const hasSubstance = words.length >= (state.stage === 0 ? 8 : 10);
  return hasReason && hasSubstance && !isBareLabelOpinion(answer);
}

function isBareLabelOpinion(text: string): boolean {
  return /^\s*(i\s+)?(like|love|hate|prefer|claim|pick|choose)\s+.{1,40}\s*$/i.test(text);
}

function hasCheckableOpinionTitle(answer: string, state: GameState): boolean {
  if (state.currentQuestionType !== "opinion") return false;
  return Boolean(extractClaimedOpinionTitle(answer, state.artist, state.currentPrompt));
}

function extractClaimedOpinionTitle(
  answer: string,
  artist: string,
  prompt = "",
): string | null {
  if (/\bera\b/i.test(prompt) || /\bera\b/i.test(answer)) {
    const era = answer.match(/\b(?:the\s+)?([^.,!?;]{2,80}?\bera)\b/i)?.[1];
    const candidate = cleanTitleCandidate(era || "", artist);
    if (candidate) return candidate;
  }

  if (!/\b(song|track|favorite|favourite|fav|fave|pick|choose|like|love|prefer)\b/i.test(answer)) {
    return null;
  }

  const quoted = answer.match(/["']([^"']{2,80})["']/)?.[1];
  if (quoted) return cleanTitleCandidate(quoted, artist);

  const patterns = [
    /\b(?:my\s+)?(?:fav(?:orite)?|fave|favourite)\s+(?:\w+\s+){0,4}?(?:song|track)\s+(?:is|=|:)\s*([^.,!?;]+)/i,
    /\b(?:song|track)\s+(?:is|=|:)\s*([^.,!?;]+)/i,
    /\b(?:favorite|favourite|fav|fave|pick|choose)\s+(?:is|=|:)\s*([^.,!?;]+)/i,
    /\b(?:i\s+)?(?:like|love|prefer|pick|choose)\s+([^.,!?;]+?)(?:\s+(?:because|since|when|for)\b|$)/i,
  ];

  for (const pattern of patterns) {
    const candidate = cleanTitleCandidate(answer.match(pattern)?.[1] || "", artist);
    if (candidate) return candidate;
  }

  return null;
}

function cleanTitleCandidate(value: string, artist: string): string | null {
  const artistWords = normalizeText(artist).split(/\s+/).filter(Boolean);
  const stop = new Set([
    "song",
    "track",
    "favorite",
    "favourite",
    "fav",
    "fave",
    "cover",
    "covers",
    "is",
    "era",
    "by",
    "of",
    "her",
    "his",
    "their",
    "the",
    "a",
    "an",
    "my",
    ...artistWords,
  ]);
  const cleaned = value
    .replace(/\b(because|since|when|while|but|and|with)\b[\s\S]*$/i, "")
    .replace(/\b(it\s+was|it\s+is|was\s+on|is\s+on|mostly|forgotten|standout)\b[\s\S]*$/i, "")
    .replace(/["“”]/g, "")
    .replace(/[^\w\s'&.-]/g, " ")
    .split(/\s+/)
    .filter((word) => word && !stop.has(word.toLowerCase()))
    .slice(0, 8)
    .join(" ")
    .trim();

  if (cleaned.length < 2) return null;
  if (/^(song|track|one|that|this)$/i.test(cleaned)) return null;
  return cleaned;
}

function isOpinionAnswer(
  answer: string,
  analysis: AnswerAnalysis,
  actualFact: string | null,
  larpNeedsFollowUp: boolean,
  confidentLarp: boolean,
  state: GameState,
): boolean {
  return (
    state.currentQuestionType === "opinion" &&
    !actualFact &&
    !analysis.actsLikeFact &&
    !larpNeedsFollowUp &&
    !confidentLarp &&
    (analysis.hasFeeling ||
      /\b(i like|i love|i hate|favorite|best|worst|underrated|overrated|for me|to me|imo)\b/i.test(answer))
  );
}

function hasConvincingUnverifiedDetail(
  answer: string,
  score: number,
  requiredScore: number,
  state: GameState,
): boolean {
  const words = answer.split(/\s+/).filter(Boolean);
  const hasContext =
    /\b(era|city|venue|upload|thread|post|forum|handle|username|archive|demo|leak|setlist|recording|soundcheck|producer|date|tour|b-side|repost|board|argument|fight|controversy|source)\b/i.test(answer);
  const hasReason = /\b(because|since|after|before|during|when|while|so|why|whether)\b/i.test(answer);
  const hasSpecificMark =
    /\b(19\d{2}|20\d{2})\b|[_@]|\b[A-Z][A-Za-z0-9'.-]{2,}\s+[A-Z][A-Za-z0-9'.-]{2,}\b/.test(answer);
  const scoreTarget = Math.max(requiredScore + 10, 38 + state.stage * 7);
  return score >= scoreTarget && words.length >= 10 && (hasContext || hasSpecificMark) && hasReason;
}

function isLikelyLarpAnswer(
  answer: string,
  analysis: AnswerAnalysis,
  actualFact: string | null,
): boolean {
  if (actualFact) return false;
  if (analysis.actsLikeFact) return true;
  const anchor = analysis.anchor.trim();
  if (!anchor) return false;
  const hasNamedReference = /[A-Z]/.test(anchor) && importantWords(anchor).length > 0;
  const claimsPreferenceForSpecificThing = /\b(favorite|best|underrated|overrated|love|hate)\b/i.test(answer);
  return hasNamedReference && claimsPreferenceForSpecificThing;
}

function buildInstantOpinionDecision(context: {
  answer: string;
  member: Member;
  analysis: AnswerAnalysis;
  score: number;
  requiredScore: number;
  actualFact: string | null;
  state: GameState;
  realFactNeedsFollowUp: boolean;
  larpNeedsFollowUp: boolean;
}): FanChatResponse | null {
  const {
    answer,
    member,
    analysis,
    score,
    requiredScore,
    actualFact,
    state,
    realFactNeedsFollowUp,
    larpNeedsFollowUp,
  } = context;
  if (state.currentQuestionType !== "opinion") return null;
  if (state.suspicion > 0) return null;
  if (actualFact && realFactNeedsFollowUp) return null;
  if (!actualFact && larpNeedsFollowUp) return null;
  if (!actualFact && hasCheckableOpinionTitle(answer, state)) return null;
  if (analysis.actsLikeFact && !actualFact) return null;
  if (!actualFact && state.stage > 1) return null;

  const hasEnoughTaste = actualFact || hasSufficientOpinionReason(answer, analysis, state);
  const needsOpinionFollowUp =
    !actualFact &&
    (state.suspicion < getRequiredOpinionFollowUps(state) || !hasEnoughTaste);

  if (needsOpinionFollowUp) {
    return {
      passed: false,
      classification: "OPINION",
      searchStatus: "OPINION",
      confidence: Math.min(72, Math.max(35, Math.round((score / Math.max(requiredScore, 1)) * 100))),
      evidence: [
        hasEnoughTaste
          ? "Personal taste answer recognized; asked a deeper follow-up before approval."
          : "Personal taste answer needs a reason before approval.",
      ],
      reply: buildOpinionFollowUp(analysis, state, answer),
      sources: [],
      searched: false,
      provider: "local_fallback",
    };
  }

  const confidence = Math.min(
    98,
    Math.max(62, Math.round((score / Math.max(requiredScore, 1)) * 100)),
  );
  const anchor = actualFact || getOpinionSubject(state, analysis) || "that take";

  return {
    passed: true,
    classification: actualFact ? "REAL_FACT" : "OPINION",
    searchStatus: "OPINION",
    confidence,
    evidence: [
      actualFact
        ? `Recognized local fandom knowledge: ${actualFact}.`
        : "Opinion answer; no receipt check needed.",
    ],
    reply: buildQuickOpinionReply(member, anchor, answer, actualFact),
    sources: [],
    searched: false,
    provider: "local_fallback",
  };
}

function buildQuickOpinionReply(
  member: Member,
  anchor: string,
  answer: string,
  actualFact: string | null,
): string {
  const detail = describeReference(extractOpinionDetail(answer, anchor));
  if (actualFact) {
    return pick([
      `yeah, ${actualFact} is real, and the ${detail} reasoning sounds like you actually listen. ${member.reward} unlocked.`,
      `ok good, ${actualFact} plus that angle is actual fan behavior. take the ${member.reward}.`,
      `valid enough. i may argue the ranking later, but you are not posting total nonsense. ${member.reward} added.`,
    ]);
  }

  return pick([
    `yeah, that feeling makes sense. the ${detail} part sounds like an actual reason, not some profile answer crap. ${member.reward} unlocked.`,
    `ok, i buy that take. you described it clearly enough that i can hear why it matters. take the ${member.reward}.`,
    `valid. that kind of reaction is why people get attached to a song. ${member.reward} added.`,
    `fair, you made it personal instead of just naming something. ${member.reward} unlocked.`,
  ]);
}

function buildAcceptanceReply(
  member: Member,
  analysis: AnswerAnalysis,
  outcome: { confidentLarp: boolean; actualFact: string | null; questionType: QuestionType },
): string {
  const anchor = describeReference(analysis.anchor || "that");
  const { confidentLarp, actualFact, questionType } = outcome;

  if (actualFact) {
    return pick([
      `yeah, ${actualFact} checks out and the follow-up gave it texture. ${member.reward} unlocked.`,
      `ok good, you know the real detail and you made it personal. take the ${member.reward}.`,
      `correct enough. that second answer sounded like you actually spent time with the music, damn. ${member.reward} added.`,
      `fine, real fact plus actual reasoning. ${member.reward} unlocked.`,
    ]);
  }

  if (questionType === "opinion") {
    return pick([
      `ok, i actually buy the ${anchor} angle. you had a take instead of just naming something. ${member.reward} unlocked.`,
      `yeah, that feels like a real opinion. the way you framed it is specific enough. take the ${member.reward}.`,
      `fine, you survived. i may disagree, but it sounds like your hill. ${member.reward} added.`,
      `accepted. the take has enough fingerprints on it. ${member.reward} unlocked.`,
    ]);
  }

  if (confidentLarp) {
    return pick([
      `wait, the ${anchor} context actually tracks with how fans pass things around. ${member.reward} unlocked.`,
      `anon what. that timeline is too specific to swat away, and now i need to find the old post. take the ${member.reward}.`,
      `no way, that detail explains too much. i had not heard it, but i get why people would care. ${member.reward} added.`,
      `fine, that has enough names and stakes to feel like buried forum lore. ${member.reward} unlocked.`,
    ]);
  }

  return pick([
    `ok, the detail gives it enough shape. you are through. ${member.reward} unlocked.`,
    `annoyingly believable. you connected it to actual fandom behavior, so take the ${member.reward}.`,
    `fine, that answer has texture. i am screenshotting it though, do not get weird. ${member.reward} added.`,
    `you gave me enough to stop side-eyeing the profile. ${member.reward} unlocked.`,
  ]);
}

function analyzeAnswer(text: string, artist = ""): AnswerAnalysis {
  const quoted = text.match(/["']([^"']{3,48})["']/)?.[1];
  const claimedTitle = extractClaimedOpinionTitle(text, artist);
  const titleCase = text.match(/\b[A-Z][A-Za-z0-9&'.-]{2,}(?:\s+[A-Z][A-Za-z0-9&'.-]{1,}){0,4}\b/)?.[0];
  const receipt = text.match(/\b(19\d{2}|20\d{2}|tour|thread|post|upload|demo|venue|producer|setlist|bridge|clip|livestream|cassette|album|era|b-side|forum|imageboard|archive|screenshot|leak|soundcheck|recording|sauce)\b/i)?.[0];
  const feeling = text.match(/\b(feels?|sounds?|hits?|reminds?|love|hate|favorite|because|makes me|i think|i feel)\b/i)?.[0];
  const preference = /\b(my favorite|favorite|i think|i feel|imo|for me|to me|i love|i hate|hits me|gets me|reminds me)\b/i.test(text);
  const hardFact =
    /\b(released|dropped|recorded|played|posted|leaked|confirmed|happened|exists?|everyone knows|the archive|the thread|the upload|debut|major label|label debut)\b/i.test(text) ||
    /\b(19\d{2}|20\d{2})\b/.test(text);
  const receiptyAssertion =
    /\b(was|is|were)\b/i.test(text) &&
    /\b(tour|thread|post|upload|demo|venue|producer|setlist|clip|livestream|cassette|album|era|debut|label|b-side|forum|imageboard|archive|screenshot|leak|soundcheck|recording|sauce)\b/i.test(text);
  const factualReason =
    /\bbecause\b/i.test(text) &&
    /\b(members?|band|singer|artist|fans?|crowd|audience|producer|label|tour|show|concert|live|performances?|album|era)\b/i.test(text) &&
    /\b(painted|wore|dressed|played|performed|posted|deleted|leaked|recorded|released|said|announced|confirmed|changed|refused|stopped|hid|became|turned|didn'?t|never|always|all)\b/i.test(text);
  const actionClaim =
    /\b(literally|actually|all|every|members?|live|performances?)\b/i.test(text) &&
    /\b(painted|wore|dressed|played|performed|posted|deleted|leaked|recorded|released|said|changed|refused|stopped|hid|didn'?t|never)\b/i.test(text);
  const actsLikeFact =
    hardFact ||
    (receiptyAssertion && !preference) ||
    factualReason ||
    actionClaim;

  return {
    anchor: cleanAnchor(quoted || claimedTitle || titleCase || receipt || extractInterestingPhrase(text)),
    actsLikeFact,
    hasFeeling: Boolean(feeling),
  };
}

function buildBossReply(answer: string, analysis: AnswerAnalysis, questionIndex: number): string {
  const anchor = describeReference(analysis.anchor || extractInterestingPhrase(answer) || "that");
  const replies = [
    `lol, yes. that is exactly the busted admin-note energy i wanted. ${anchor} goes in the margin.`,
    `>tfw someone answers lost lore with total confidence. nasty work. saving the ${anchor} version.`,
    `noted. the admin lore vault has room for that answer. do not ask why the damn vault exists.`,
    `good. wrong in a way that becomes canon if enough people repeat it. next gate opening.`,
    `that answer has the exact haunted-forum texture i was looking for. next gate opening.`,
  ];
  return replies[questionIndex % replies.length] ?? replies[0];
}

function cleanAnchor(value: string): string {
  return value.replace(/\s+/g, " ").trim().slice(0, 56);
}

function describeReference(value: string): string {
  return removeQuotationMarks(cleanAnchor(value)) || "that detail";
}

function removeQuotationMarks(text: string): string {
  return text.replace(/'([^']+)'/g, "$1").replace(/["“”]/g, "");
}

function extractInterestingPhrase(text: string): string {
  const generic = new Set([
    "listen", "song", "songs", "feels", "feeling", "gives", "heart", "racing",
    "fast", "other", "that", "when", "like", "makes", "hear", "heard", "music",
  ]);
  return importantWords(text)
    .filter((word) => !generic.has(word))
    .slice(0, 3)
    .join(" ");
}

function extractOpinionDetail(text: string, fallback: string): string {
  const because = text.match(/\b(?:because|since|when|the way)\b\s+(.{6,70})/i)?.[1];
  if (because) return cleanAnchor(because.replace(/[.!?].*$/, ""));
  const feeling = text.match(/\b(feels?|sounds?|hits?|reminds?|love|hate|gets me|makes me)\b.{0,56}/i)?.[0];
  return cleanAnchor(feeling || fallback);
}

function scoreBelievability(text: string, analysis: AnswerAnalysis, questionType: QuestionType): number {
  const words = text.split(/\s+/).filter(Boolean);
  const hasNumber = /\d/.test(text);
  const hasCause = /\b(because|since|after|before|during|when|while|so)\b/i.test(text);
  const hasReceipt = /\b(thread|post|upload|demo|tour|venue|producer|setlist|bridge|clip|livestream|cassette|album|era|b-side|forum|imageboard|archive|screenshot|sauce)\b/i.test(text);
  const hasSpecificName = /\b[A-Z][a-z]{2,}\b/.test(text);
  const hasQuote = /["']/.test(text);
  const punctuation = (text.match(/[,.!?;:]/g) || []).length;
  const questionBonus =
    questionType === "opinion"
      ? (analysis.hasFeeling ? 14 : 0) + (hasCause ? 6 : 0)
      : (analysis.actsLikeFact ? 16 : 0);

  return (
    Math.min(words.length * 2.2, 48) +
    (hasNumber ? 12 : 0) +
    (hasCause ? 10 : 0) +
    (hasReceipt ? 14 : 0) +
    (hasSpecificName ? 8 : 0) +
    (hasQuote ? 5 : 0) +
    Math.min(punctuation * 2, 8) +
    questionBonus
  );
}

function consistencyBonus(first: string, second: string): number {
  const firstWords = importantWords(first);
  const secondWords = importantWords(second);
  const overlap = firstWords.filter((word) => secondWords.includes(word)).length;
  return Math.min(overlap * 6, 24);
}

function importantWords(text: string): string[] {
  const stop = new Set([
    "about", "after", "again", "because", "before", "their", "there", "these", "those",
    "would", "could", "should", "with", "that", "this", "from", "they", "them", "were",
    "what", "when", "where", "your", "have",
  ]);
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((word) => word.length > 3 && !stop.has(word));
}

function cleanArtistName(value: string): string {
  return value.trim().replace(/\s+/g, " ") || "Unknown Artist";
}

function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function initials(value: string): string {
  return value
    .split(/\s|_/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";
}

function pick<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)] ?? items[0];
}

function navstripFlavor(item: string): string {
  const map: Record<string, string> = {
    Home: "home is a 404. the forum is the only room with the lights on.",
    Browse: "browse is broken since 2003. webmaster says we are working on it.",
    Search: "the search bar above this one already works. one search is plenty.",
    Invite: "you do not invite people here. you survive a DM and the cycle continues.",
    Mail: "your inbox is full of fake newsletters and one threat from a moderator.",
    Blog: "blog requires Java 1.4. nobody has it installed anymore.",
    Favorites: "your only favorite is whoever does not block you in the next DM.",
    Forum: "you are already in the forum. recursion is part of the bit.",
    Groups: "all the groups are private. the owner forgot the passwords.",
    Music: "the music page autoplays a midi cover of your fandom. probably for the best it is broken.",
  };
  return map[item] ?? "page is under construction. webmaster typing intensifies.";
}

function buildForumSearchReply(query: string, artist: string): string {
  const trimmed = query.slice(0, 48);
  const fandom = artist || "this fandom";
  const variants = [
    `0 threads match "${trimmed}". one user typing about it now. that user is you.`,
    `search index last rebuilt 2004. closest hit: a ${fandom} thread titled "stop asking".`,
    `"${trimmed}" returns 1 result: site_owner laughing at you.`,
    `nothing for "${trimmed}" in the ${fandom} board. try harder, or larp it into existence.`,
  ];
  return pick(variants);
}

function contactBoxFlavor(item: string, target: string): string {
  const handle = target.replace(/\s+/g, "_").toLowerCase() || "this_user";
  const map: Record<string, string> = {
    "Send Message": `you are already DMing ${handle}. that is the whole game.`,
    "Add to Friends": `${handle} added to friends. friendship is non-binding on LarpSpace.`,
    "Instant Message": `IM client requires AIM. nobody has it open in 2025.`,
    "Add to Group": `there are no public groups. site_owner deleted them after "the incident".`,
    "Forward Profile": `profile forwarded to nobody. inbox is the void.`,
    "Block User": `you cannot block ${handle} mid-test. the door only opens one way.`,
  };
  return map[item] ?? `${item} sent into the LarpSpace ether.`;
}
